// Resources.jsx
import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO.jsx';
import Header from "../partials/header/Header.jsx";
import Footer from '../container/Footer/Footer.js';
import ScrollToTop from '../components/ScrollToTop.jsx';

const Resources = () => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedResource, setSelectedResource] = useState(null);
    const [email, setEmail] = useState('');
    const [downloadLoading, setDownloadLoading] = useState(false);

    // Button gradient colors for each position in the row (0,1,2)
    const buttonGradients = [
        'linear-gradient(90deg, rgb(108, 60, 201), rgb(138, 99, 210))',  // 1st card in row - Purple
        'linear-gradient(90deg, rgb(233, 43, 110), rgb(242, 124, 124))', // 2nd card in row - Pink/Red
        'linear-gradient(90deg, rgb(57, 180, 191), rgb(87, 201, 179))',   // 3rd card in row - Teal/Green
    ];

    useEffect(() => {
        fetchResources();
    }, []);

    const fetchResources = async () => {
        try {
            setLoading(true);
            const response = await fetch('https://admin.lumademand.com/public/api/resources');
            const result = await response.json();
            
            if (result.success) {
                setResources(result.data);
                setError(null);
            } else {
                setError('Failed to load resources');
            }
        } catch (err) {
            setError('Error fetching resources: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadClick = (resource) => {
        setSelectedResource(resource);
        setShowEmailModal(true);
    };

    const handleDownloadSubmit = async (e) => {
        e.preventDefault();
        
        if (!email) {
            alert('Please enter your email address');
            return;
        }

        setDownloadLoading(true);

        try {
            const response = await fetch(`https://admin.lumademand.com/public/api/resources/${selectedResource.id}/download`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: email })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${selectedResource.title}.${selectedResource.file_type}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                
                setShowEmailModal(false);
                setEmail('');
                setSelectedResource(null);
                fetchResources();
            } else {
                const errorData = await response.json();
                alert(errorData.message || 'Download failed. Please try again.');
            }
        } catch (err) {
            alert('Error downloading file: ' + err.message);
        } finally {
            setDownloadLoading(false);
        }
    };

    const getFileIconClass = (fileType) => {
        const icons = {
            pdf: 'fa-file-pdf',
            ppt: 'fa-file-powerpoint',
            pptx: 'fa-file-powerpoint',
            doc: 'fa-file-word',
            docx: 'fa-file-word',
            xls: 'fa-file-excel',
            xlsx: 'fa-file-excel',
            txt: 'fa-file-alt',
            zip: 'fa-file-archive',
            jpg: 'fa-file-image',
            jpeg: 'fa-file-image',
            png: 'fa-file-image',
        };
        return icons[fileType] || 'fa-file';
    };

    // Get gradient for button based on position in row (0,1,2)
    const getButtonGradient = (index) => {
        return buttonGradients[index % buttonGradients.length];
    };

    if (loading) {
        return (
            <>
                <SEO title="Resources" />
                <Header />
                <div className="container py-5 my-5">
                    <div className="text-center py-5">
                        <div className="spinner-border text-danger" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading resources...</p>
                    </div>
                </div>
                <Footer />
                <ScrollToTop />
            </>
        );
    }

    if (error) {
        return (
            <>
                <SEO title="Resources" />
                <Header />
                <div className="container py-5 my-5">
                    <div className="text-center py-5">
                        <i className="fas fa-exclamation-circle text-danger" style={{ fontSize: '4rem' }}></i>
                        <h2 className="mt-3">Error Loading Resources</h2>
                        <p className="text-muted">{error}</p>
                    </div>
                </div>
                <Footer />
                <ScrollToTop />
            </>
        );
    }

    // Show "No Resources" message if resources array is empty
    if (resources.length === 0) {
        return (
            <>
                <SEO title="Resources" />
                <Header />
                <div className="bg-light py-5" style={{ minHeight: '70vh' }}>
                    <div className="container py-5 my-5">
                        <div className="text-center py-5 mt-5">
                            <div className="mb-4">
                                <i className="fas fa-folder-open fa-5x text-muted"></i>
                            </div>
                            <h2 className="fw-bold mb-3">No Resources Available</h2>
                            <p className="lead text-muted mb-4">
                                We&apos;re currently preparing valuable resources for you.
                            </p>
                            <p className="text-muted">
                                New resources will be added shortly. Please check back soon!
                            </p>
                            <div className="mt-4">
                                <div className="d-flex justify-content-center gap-3">
                                    <div className="text-center">
                                        <div className="mb-2">
                                            <i className="fas fa-file-pdf fa-2x" style={{ color: '#dc3545' }}></i>
                                        </div>
                                        <small className="text-muted">PDF Guides</small>
                                    </div>
                                    <div className="text-center">
                                        <div className="mb-2">
                                            <i className="fas fa-file-powerpoint fa-2x" style={{ color: '#fd7e14' }}></i>
                                        </div>
                                        <small className="text-muted">Presentations</small>
                                    </div>
                                    <div className="text-center">
                                        <div className="mb-2">
                                            <i className="fas fa-file-word fa-2x" style={{ color: '#0d6efd' }}></i>
                                        </div>
                                        <small className="text-muted">Documents</small>
                                    </div>
                                    <div className="text-center">
                                        <div className="mb-2">
                                            <i className="fas fa-file-excel fa-2x" style={{ color: '#198754' }}></i>
                                        </div>
                                        <small className="text-muted">Spreadsheets</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <ScrollToTop />
            </>
        );
    }

    // Split resources into rows of 3 for consistent button colors
    const rows = [];
    for (let i = 0; i < resources.length; i += 3) {
        rows.push(resources.slice(i, i + 3));
    }

    return (
        <>
            <SEO title="Resources" />
            <Header />
            
            <div className="bg-light py-5" style={{ minHeight: '70vh' }}>
                <div style={{marginTop:'3rem'}} className="container py-4">
                    {/* Header Section */}
                    <div className="text-center mb-5 mt-5">
                        <h1 className="display-4 fw-bold mb-3">Resources</h1>
                        <p className="lead text-muted">Download our resources for free</p>
                    </div>

                    {/* Resources Rows */}
                    {rows.map((row, rowIndex) => (
                        <div key={rowIndex} className="row g-4 mb-4">
                            {row.map((resource, colIndex) => (
                                <div key={resource.id} className="col-md-6 col-lg-4 d-flex">
                                    <div className="card h-100 w-100 shadow-sm border-0 resource-card d-flex flex-column">
                                        {/* File Icon/Thumbnail */}
                                        <div className="card-body text-center pt-4 pb-3">
                                            {resource.thumbnail_url ? (
                                                <img 
                                                    src={resource.thumbnail_url} 
                                                    alt={resource.title}
                                                    className="img-fluid mb-3"
                                                    style={{ height: '80px', objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <i 
                                                    className={`fas ${getFileIconClass(resource.file_type)} fa-4x mb-3`}
                                                    style={{ color: '#6c757d' }}
                                                ></i>
                                            )}
                                            
                                            <h5 className="card-title fw-bold mt-2">{resource.title}</h5>
                                            <p className="card-text text-muted small">
                                                {resource.description && resource.description.length > 100 
                                                    ? resource.description.substring(0, 100) + '...' 
                                                    : resource.description}
                                            </p>
                                        </div>
                                        
                                        {/* Meta Info - always at bottom */}
                                        <div className="mt-auto p-3 pt-0">
                                            <div className="d-flex justify-content-between align-items-center mb-3 small">
                                                <span className="badge bg-light text-dark px-3 py-2">
                                                    <i className="fas fa-tag me-1"></i> {resource.file_type.toUpperCase()}
                                                </span>
                                                <span className="text-muted">
                                                    <i className="fas fa-hdd me-1"></i> {resource.file_size}
                                                </span>
                                                <span className="text-muted">
                                                    <i className="fas fa-download me-1"></i> {resource.download_count}
                                                </span>
                                            </div>
                                            
                                            <button 
                                                onClick={() => handleDownloadClick(resource)}
                                                className="btn w-100 py-2 fw-bold text-white"
                                                style={{ 
                                                    background: getButtonGradient(colIndex),
                                                    border: 'none'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = '#000';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = getButtonGradient(colIndex);
                                                }}
                                            >
                                                <i className="fas fa-download me-2"></i> Download
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* Email Modal - Bootstrap Modal */}
            {showEmailModal && (
                <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Download Resource</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowEmailModal(false)}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Please enter your email address to download:</p>
                                <div className="alert alert-light text-center border">
                                    <strong>{selectedResource?.title}</strong>
                                </div>
                                <form onSubmit={handleDownloadSubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="email" className="form-label">Email Address *</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="your@email.com"
                                            required
                                            disabled={downloadLoading}
                                        />
                                    </div>
                                    <button 
                                        type="submit" 
                                        className="btn w-100 py-2 fw-bold text-white"
                                        style={{ 
                                            background: selectedResource 
                                                ? (() => {
                                                    const resourceIndex = resources.findIndex(r => r.id === selectedResource.id);
                                                    const colIndex = resourceIndex % 3;
                                                    return getButtonGradient(colIndex);
                                                  })()
                                                : buttonGradients[0],
                                            border: 'none'
                                        }}
                                        disabled={downloadLoading}
                                    >
                                        {downloadLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-download me-2"></i> Download Now
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
            <ScrollToTop />

            {/* Minimal custom CSS */}
            <style>{`
                .resource-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    overflow: hidden;
                    border-radius: 12px;
                }
                .resource-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                }
                @keyframes slideIn {
                    from {
                        transform: translateY(-50px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
                .modal.show .modal-dialog {
                    animation: slideIn 0.3s ease;
                }
                .badge {
                    font-weight: 500;
                }
                .card {
                    border-radius: 12px;
                    overflow: hidden;
                }
                .btn {
                    transition: all 0.3s ease;
                }
            `}</style>
        </>
    );
};

export default Resources;